import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Filter, X, User, Maximize2, Minimize2, Pencil, Check,
  Phone, ChevronDown, ChevronLeft, ChevronRight, Sparkles, Plus,
} from "lucide-react";
import useCrmApi from "../../../components/hook/crm-req";
import {
  ActionsTab,
  ActivityTimeline,
  CALL_OUTCOMES,
  CALL_PURPOSES,
  CallOutcome,
  DealStageDropdown,
  FieldSelect,
  formatSlotLabel,
  formatTimezoneLabel,
  InsightDataTab,
  REMINDER_OPTIONS,
  ScheduleCallPayload,
  ScheduledCall,
  TIME_SLOT_VALUES,
  TIMEZONE_OPTIONS,
  WEEKDAY_LABELS,
  buildCalendarGrid,
  isSameDay,
  useConversationInsights,
  useConversationNotes,
  useDealProgress,
  useScheduledCalls,
  zonedDateAndTimeFromUtc,
  zonedTimeToUtcIso,
} from "../../../components/crm/conversation/components/insight";
import type { Conversation, ConversationInsights, ConversationNote, DealStage, ChatMessage } from "../../crm-pages/conversation/types";
import { formatDateTime } from "../../../utils/function";
import { TeamDetails } from "../../../components/crm/customer/types";
import { MessageComponent } from "../../../components/crm/conversation/components/avatar";
import { FormPreviewPanel } from "../../../components/crm/conversation/components/form-preview-panel";
import {
  FullLead,
  LeadRecord,
  LeadUpdatePatch,
  StatusPill,
  ChannelLabel,
  fetchFullLead,
  formatSourceLabel,
  goToLeadConversation,
  recordToContactItem,
  updateLeadSafely,
} from "./shared";

type LeadDetailsTab = "Actions" | "Insight" | "Details";

const formatDatePill = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return "Today";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const CollapsibleSection: React.FC<{ title: string; children: React.ReactNode; defaultOpen?: boolean }> = ({
  title,
  children,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100 dark:border-gray-800">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-transparent border-none cursor-pointer"
      >
        <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">{title}</span>
        <ChevronDown size={15} className={`text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 gap-2">
    <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0">{label}</span>
    <span className="text-xs font-medium text-gray-800 dark:text-gray-200 text-right truncate">{value || "—"}</span>
  </div>
);

// Score thresholds match the API's own defaults for /leads/{id}/score —
// hot 80-100, warm 50-79, cold 1-49. This is a display label ("Lead
// temperature"), never to be confused with `lead.status` (lifecycle) or the
// conversation's `deal_stage` — three separate concepts the API tracks
// independently.
const temperatureFromScore = (score: number): string => {
  if (score >= 80) return "Hot";
  if (score >= 50) return "Warm";
  if (score > 0) return "Cold";
  return "New";
};

const formatDealPotential = (dealPotential?: { amount_minor: number; currency: string } | null): string | undefined => {
  if (!dealPotential) return undefined;
  try {
    const formatter = new Intl.NumberFormat(undefined, { style: "currency", currency: dealPotential.currency });
    const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
    return formatter.format(dealPotential.amount_minor / 10 ** digits);
  } catch {
    return undefined;
  }
};

// Name shown at the top of the drawer — prefer the lead's own display_name,
// fall back to first_name + last_name, then to the list row's precomputed
// name while the full record is still loading.
const displayNameFor = (fullLead: FullLead | null, fallback: string): string => {
  if (fullLead?.display_name) return fullLead.display_name;
  const composed = [fullLead?.first_name, fullLead?.last_name].filter(Boolean).join(" ").trim();
  return composed || fallback;
};

// Inline note editor — the expanded state a collapsed "New Note" (or an
// existing note's "Edit Note" link) opens into: a draft textarea with an AI
// "Suggest" affordance on the left and Discard / Save Note on the right,
// rendered in place rather than as a popup so it reads as part of the same
// Call Notes section it belongs to.
const NoteComposer: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onDiscard: () => void;
  saving: boolean;
}> = ({ value, onChange, onSave, onDiscard, saving }) => (
  <div>
    <textarea
      value={value}
      maxLength={300}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Your note"
      rows={4}
      autoFocus
      className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 outline-none focus:border-indigo-400 resize-none mb-1"
    />
    <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 text-right mb-2">{value.length}/300</p>
    <div className="flex items-center justify-between">
      <button
        type="button"
        onClick={() => toast.info("AI note suggestions are coming soon.")}
        className="flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-transparent border-none cursor-pointer p-0 hover:text-indigo-700 dark:hover:text-indigo-300"
      >
        <Sparkles size={14} />
        Suggest
      </button>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onDiscard}
          className="text-sm font-medium text-gray-500 dark:text-gray-400 bg-transparent border-none cursor-pointer p-0 hover:text-gray-700 dark:hover:text-gray-300"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!value.trim() || saving}
          className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : "Save Note"}
        </button>
      </div>
    </div>
  </div>
);

// ── Schedule a Call panel ────────────────────────────────────────────────────
// Renders in place of the conversation thread (right column), not as a
// floating dialog — matches the approved design exactly. Backed by the real
// call-scheduling API (conversation-call-scheduling-integration.md) via the
// shared useScheduledCalls hook — the calendar/timezone helpers and field
// list constants are shared with every other <ActionsTab> surface (insight.tsx).

const ScheduleCallPanel: React.FC<{
  leadName: string;
  teamMembers: TeamDetails[];
  defaultAssignedTo?: string | null;
  conversationId?: string | null;
  callsApi: ReturnType<typeof useScheduledCalls>;
  editingCall: ScheduledCall | null;
  onClose: () => void;
}> = ({ leadName, teamMembers, defaultAssignedTo, conversationId, callsApi, editingCall, onClose }) => {
  const isEditing = !!editingCall;
  const [mode, setMode] = useState<"schedule" | "complete">("schedule");
  const [assignedTo, setAssignedTo] = useState("");
  const [purpose, setPurpose] = useState(CALL_PURPOSES[0].value);
  const [reminderValue, setReminderValue] = useState("15");
  const [notes, setNotes] = useState("");
  const [timezone, setTimezone] = useState(TIMEZONE_OPTIONS[0]);
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [savingNote, setSavingNote] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [outcome, setOutcome] = useState<CallOutcome>("connected");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [outcomeNotes, setOutcomeNotes] = useState("");
  const [completing, setCompleting] = useState(false);

  // Call Notes — a saved-notes list (shared with "Add Engagement Notes"
  // elsewhere via the same conversation-notes backend), rendered inline here
  // instead of via a popup: a collapsed "New Note" trigger expands into a
  // draft textarea (Suggest / Discard / Save Note), and each existing note
  // can be reopened the same way via its own "Edit Note" link.
  const { notes: conversationNotes, loadingNotes, addNote, editNote, deleteNote } = useConversationNotes(conversationId);
  const [noteComposer, setNoteComposer] = useState<{ mode: "new" | "edit"; noteId?: string } | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  // Reset (or pre-fill from editingCall) whenever the panel is opened for a
  // different call, rather than carrying over whatever was left from before.
  useEffect(() => {
    setMode("schedule");
    const pf = editingCall ? zonedDateAndTimeFromUtc(editingCall.scheduled_at, editingCall.time_zone) : null;
    setAssignedTo(editingCall?.assigned_to?.user_id || defaultAssignedTo || "");
    setPurpose(editingCall?.purpose || CALL_PURPOSES[0].value);
    setReminderValue(
      editingCall
        ? editingCall.reminder_minutes_before != null ? String(editingCall.reminder_minutes_before) : "none"
        : "15",
    );
    setNotes(editingCall?.notes || "");
    const zone =
      editingCall?.time_zone ||
      (() => {
        const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return TIMEZONE_OPTIONS.includes(detected) ? detected : TIMEZONE_OPTIONS[0];
      })();
    setTimezone(zone);
    const month = pf ? new Date(pf.date) : new Date();
    month.setDate(1);
    setViewMonth(month);
    setSelectedDate(pf?.date || null);
    setSelectedTime(pf?.timeValue || null);
    setOutcome("connected");
    setDurationMinutes("");
    setOutcomeNotes("");
    setNoteComposer(null);
    setNoteDraft("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingCall?.id]);

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const grid = buildCalendarGrid(viewMonth);

  const openNewNoteComposer = () => {
    setNoteComposer({ mode: "new" });
    setNoteDraft("");
  };

  const openEditNoteComposer = (note: ConversationNote) => {
    setNoteComposer({ mode: "edit", noteId: note.id });
    setNoteDraft(note.description);
  };

  const closeNoteComposer = () => {
    setNoteComposer(null);
    setNoteDraft("");
  };

  const handleSaveNote = async () => {
    if (!noteDraft.trim() || savingNote || !noteComposer) return;
    if (!conversationId) {
      toast.error("No conversation to attach this note to yet.");
      return;
    }
    setSavingNote(true);
    const ok =
      noteComposer.mode === "edit" && noteComposer.noteId
        ? await editNote(noteComposer.noteId, noteDraft.trim())
        : await addNote(noteDraft.trim());
    setSavingNote(false);
    if (ok) {
      // A brand-new note also becomes this call's own notes field (same
      // single value the schedule/reschedule payload below sends) — editing
      // an older note doesn't, since that note isn't necessarily about this
      // particular call.
      if (noteComposer.mode === "new") setNotes(noteDraft.trim());
      toast.success(noteComposer.mode === "edit" ? "Note updated" : "Note saved");
      closeNoteComposer();
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (deletingNoteId) return;
    setDeletingNoteId(noteId);
    const ok = await deleteNote(noteId);
    setDeletingNoteId(null);
    if (ok) toast.success("Note deleted");
  };

  const handleSchedule = async () => {
    if (!selectedDate || !selectedTime || scheduling) return;
    const [hour, minute] = selectedTime.split(":").map(Number);
    const scheduledAtIso = zonedTimeToUtcIso(
      selectedDate.getFullYear(), selectedDate.getMonth() + 1, selectedDate.getDate(), hour, minute, timezone,
    );
    if (new Date(scheduledAtIso).getTime() <= Date.now()) {
      toast.error("Pick a date and time in the future.");
      return;
    }
    const assignedMember = teamMembers.find((m) => m.id === assignedTo);
    const reminderOption = REMINDER_OPTIONS.find((r) => r.value === reminderValue);
    const payload: ScheduleCallPayload = {
      scheduled_at: scheduledAtIso,
      time_zone: timezone,
      purpose,
      reminder_minutes_before: reminderOption?.minutes ?? null,
      notes: notes.trim() || undefined,
      ...(assignedMember
        ? {
            assigned_user_id: assignedMember.id,
            assigned_user_name: `${assignedMember.first_name} ${assignedMember.last_name}`.trim(),
            assigned_user_email: assignedMember.email,
          }
        : {}),
    };
    setScheduling(true);
    const result =
      isEditing && editingCall ? await callsApi.rescheduleCall(editingCall.id, payload) : await callsApi.scheduleCall(payload);
    setScheduling(false);
    if (result.ok) {
      toast.success(isEditing ? "Call rescheduled" : "Call scheduled");
      onClose();
    } else {
      toast.error(result.error || "Failed to save the call");
    }
  };

  const handleCancelCall = async () => {
    if (!editingCall || cancelling) return;
    setCancelling(true);
    const result = await callsApi.cancelCall(editingCall.id);
    setCancelling(false);
    if (result.ok) {
      toast.success("Call cancelled");
      onClose();
    } else {
      toast.error(result.error || "Failed to cancel the call");
    }
  };

  const handleComplete = async () => {
    if (!editingCall || completing) return;
    const minutes = parseFloat(durationMinutes);
    const durationSeconds = !Number.isNaN(minutes) && minutes >= 0 ? Math.min(86400, Math.round(minutes * 60)) : undefined;
    setCompleting(true);
    const result = await callsApi.completeCall(editingCall.id, {
      outcome,
      duration_seconds: durationSeconds,
      outcome_notes: outcomeNotes.trim() || undefined,
    });
    setCompleting(false);
    if (result.ok) {
      toast.success("Call marked as completed");
      onClose();
    } else {
      toast.error(result.error || "Failed to complete the call");
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-white dark:bg-gray-900 min-w-0 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Phone size={16} className="text-gray-500 dark:text-gray-400 shrink-0" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
            {mode === "complete" ? "Complete Call with" : isEditing ? "Reschedule Call with" : "Schedule a Call with"} {leadName}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-full text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
        >
          <X size={18} />
        </button>
      </div>

      {isEditing && (
        <div className="flex gap-1 mx-5 mt-3 mb-1 bg-gray-50 dark:bg-gray-800 rounded-lg p-1 shrink-0">
          <button
            onClick={() => setMode("schedule")}
            className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors border-none cursor-pointer ${mode === "schedule" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100" : "bg-transparent text-[#7B7B7B] font-semibold dark:text-gray-500"}`}
          >
            Reschedule
          </button>
          <button
            onClick={() => setMode("complete")}
            className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors border-none cursor-pointer ${mode === "complete" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100" : "bg-transparent text-[#7B7B7B] font-semibold dark:text-gray-500"}`}
          >
            Mark as Completed
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {mode === "complete" ? (
          <CollapsibleSection title="Call Outcome">
            <FieldSelect label="Outcome" value={outcome} onChange={(v) => setOutcome(v as CallOutcome)} options={CALL_OUTCOMES} />
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Duration (minutes, optional)</label>
            <input
              type="number"
              min={0}
              max={1440}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
              placeholder="e.g. 7"
              className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg px-3 py-2.5 text-sm text-gray-800 dark:text-gray-200 outline-none focus:border-indigo-400 mb-3"
            />
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Outcome Notes</label>
              <span className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">{outcomeNotes.length}/300</span>
            </div>
            <textarea
              value={outcomeNotes}
              maxLength={300}
              onChange={(e) => setOutcomeNotes(e.target.value)}
              placeholder="What happened on the call?"
              rows={3}
              className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 outline-none focus:border-indigo-400 resize-none"
            />
          </CollapsibleSection>
        ) : (
        <>
        <CollapsibleSection title="Add Call Details">
          <FieldSelect
            label="Contact Assigned to"
            value={assignedTo}
            onChange={setAssignedTo}
            placeholder="Select a team member"
            options={teamMembers.map((m) => ({ value: m.id, label: `${m.first_name} ${m.last_name}`.trim() }))}
          />
          <FieldSelect label="Call Purpose (Optional)" value={purpose} onChange={setPurpose} options={CALL_PURPOSES} />
          <FieldSelect label="Send Reminder" value={reminderValue} onChange={setReminderValue} options={REMINDER_OPTIONS} />

          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">Call Notes</label>

          {loadingNotes ? (
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 py-1 mb-2">Loading notes…</p>
          ) : (
            conversationNotes.length > 0 && (
              <div className="space-y-3 mb-3">
                {conversationNotes.map((note) =>
                  noteComposer?.mode === "edit" && noteComposer.noteId === note.id ? (
                    <NoteComposer
                      key={note.id}
                      value={noteDraft}
                      onChange={setNoteDraft}
                      onSave={handleSaveNote}
                      onDiscard={closeNoteComposer}
                      saving={savingNote}
                    />
                  ) : (
                    <div key={note.id}>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{note.description}</p>
                      <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5 mb-1">{formatDateTime(note.created_at)}</p>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => openEditNoteComposer(note)}
                          className="text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-transparent border-none cursor-pointer p-0 hover:text-indigo-700 dark:hover:text-indigo-300 hover:underline"
                        >
                          Edit Note
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteNote(note.id)}
                          disabled={deletingNoteId === note.id}
                          className="text-xs font-medium text-red-500 bg-transparent border-none cursor-pointer p-0 hover:text-red-600 hover:underline disabled:opacity-50"
                        >
                          {deletingNoteId === note.id ? "Deleting…" : "Delete Note"}
                        </button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            )
          )}

          {noteComposer?.mode === "new" ? (
            <NoteComposer
              value={noteDraft}
              onChange={setNoteDraft}
              onSave={handleSaveNote}
              onDiscard={closeNoteComposer}
              saving={savingNote}
            />
          ) : (
            <button
              type="button"
              onClick={openNewNoteComposer}
              className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 hover:border-indigo-300 dark:hover:border-indigo-500/50 transition-colors"
            >
              New Note
              <Plus size={16} />
            </button>
          )}
        </CollapsibleSection>

        <CollapsibleSection title="Select Call Date">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              className="p-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 cursor-pointer"
            >
              <ChevronLeft size={15} />
            </button>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {viewMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
            </span>
            <button
              onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
              className="p-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 cursor-pointer"
            >
              <ChevronRight size={15} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center mb-1">
            {WEEKDAY_LABELS.map((d) => (
              <span key={d} className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 py-1">{d}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {grid.map(({ date, inMonth }, i) => {
              const isSelected = !!selectedDate && isSameDay(date, selectedDate);
              const isToday = isSameDay(date, today);
              const isPast = date < startOfToday;
              return (
                <button
                  key={i}
                  disabled={!inMonth || isPast}
                  onClick={() => setSelectedDate(date)}
                  className={`aspect-square rounded-full text-xs font-medium transition-colors ${
                    !inMonth || isPast
                      ? "text-gray-300 dark:text-gray-700 cursor-default"
                      : isSelected
                        ? "bg-indigo-600 text-white"
                        : isToday
                          ? "border border-indigo-400 dark:border-indigo-500 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Select Time">
          <FieldSelect
            label="Time Zone"
            value={timezone}
            onChange={setTimezone}
            options={TIMEZONE_OPTIONS.map((tz) => ({ value: tz, label: formatTimezoneLabel(tz) }))}
          />
          <div className="grid grid-cols-3 gap-2 mt-1">
            {TIME_SLOT_VALUES.map((slot) => (
              <button
                key={slot}
                onClick={() => setSelectedTime(slot)}
                className={`py-2 rounded-lg text-xs font-medium border transition-colors ${
                  selectedTime === slot
                    ? "bg-indigo-600 border-indigo-600 text-white"
                    : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                {formatSlotLabel(slot)}
              </button>
            ))}
          </div>
        </CollapsibleSection>
        </>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-t border-gray-100 dark:border-gray-800 shrink-0">
        {isEditing && mode === "schedule" ? (
          <button
            onClick={handleCancelCall}
            disabled={cancelling}
            className="px-3 py-2 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 bg-transparent border-none cursor-pointer disabled:opacity-50"
          >
            {cancelling ? "Cancelling…" : "Cancel Call"}
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 transition-colors"
          >
            Close
          </button>
          {mode === "complete" ? (
            <button
              onClick={handleComplete}
              disabled={completing}
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {completing ? "Saving…" : "Mark Completed"}
            </button>
          ) : (
            <button
              onClick={handleSchedule}
              disabled={!selectedDate || !selectedTime || scheduling}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {scheduling ? "Saving…" : isEditing ? "Save Changes" : "Schedule Call"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Hover to reveal a pencil; click the value (or the pencil) to edit inline,
// then a check button saves via onSave (wired to updateLeadSafely upstream).
const EditableRow: React.FC<{
  label: string;
  value: string;
  onSave: (nextValue: string) => Promise<boolean>;
}> = ({ label, value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const ok = await onSave(draft.trim());
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <div className="flex items-center justify-between py-1.5 gap-2 group">
      <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0">{label}</span>
      {editing ? (
        <div className="flex items-center gap-1 flex-1 min-w-0 justify-end">
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") { setDraft(value); setEditing(false); }
            }}
            className="flex-1 min-w-0 text-xs text-right text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 border border-indigo-300 dark:border-indigo-500 rounded px-1.5 py-0.5 outline-none focus:border-indigo-500"
          />
          <button
            onClick={handleSave}
            disabled={saving}
            title="Save"
            className="p-1 rounded text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/15 bg-transparent border-none cursor-pointer shrink-0 disabled:opacity-50"
          >
            <Check size={13} />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="flex items-center gap-1 justify-end min-w-0 bg-transparent border-none cursor-pointer p-0 text-xs font-medium text-gray-800 dark:text-gray-200"
        >
          <span className="truncate">{value || "—"}</span>
          <Pencil size={11} className="text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        </button>
      )}
    </div>
  );
};

const DetailsPane: React.FC<{
  lead: LeadRecord;
  fullLead: FullLead | null;
  insights: ConversationInsights | null;
  loadingInsights: boolean;
  dealProgress: ReturnType<typeof useDealProgress>["dealProgress"];
  loadingDealProgress: boolean;
  updatingStage: boolean;
  updateStage: (stage: DealStage) => Promise<boolean>;
  onUpdateField: (patch: LeadUpdatePatch) => Promise<boolean>;
}> = ({ lead, fullLead, insights, loadingInsights, dealProgress, loadingDealProgress, updatingStage, updateStage, onUpdateField }) => {
  const custom = fullLead?.custom || {};
  const location = custom.location?.display_text;
  const linkedin = custom.linkedin?.url;
  const dealPotential = formatDealPotential(custom.deal_potential);
  const fullName = fullLead?.title || lead.name;

  return (
    <>
      <CollapsibleSection title="Contact Info">
        <EditableRow label="Full Name" value={fullName || ""} onSave={(v) => onUpdateField({ title: v })} />
        <EditableRow
          label="Display Name"
          value={fullLead?.display_name || ""}
          onSave={(v) => onUpdateField({ display_name: v })}
        />
        <EditableRow
          label="Phone Number"
          value={fullLead?.phone || lead.phone || ""}
          onSave={(v) => onUpdateField({ phone: v })}
        />
        <InfoRow label="Lead Temperature" value={temperatureFromScore(fullLead?.score ?? lead.score)} />
        <InfoRow label="Source" value={formatSourceLabel(fullLead?.source || lead.source) || "N.A."} />
        <EditableRow
          label="Location"
          value={location || ""}
          onSave={(v) => onUpdateField({ custom: { location: { ...custom.location, display_text: v } } })}
        />
        <EditableRow
          label="Email"
          value={fullLead?.email || lead.email || ""}
          onSave={(v) => onUpdateField({ email: v })}
        />
        <InfoRow label="LinkedIn ID" value={linkedin} />
        <InfoRow label="Channel" value={<ChannelLabel channel={lead.channel} />} />
        <InfoRow label="Status" value={<StatusPill status={fullLead?.status || lead.status} />} />
      </CollapsibleSection>
      <CollapsibleSection title="Activity Timeline">
        <ActivityTimeline timeline={insights?.timeline} loading={loadingInsights} />
      </CollapsibleSection>
      <CollapsibleSection title="Deal Progress">
        {loadingDealProgress && !dealProgress ? (
          <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 py-2">Loading deal progress…</p>
        ) : dealProgress ? (
          <>
            {dealPotential && <InfoRow label="Deal Potential" value={dealPotential} />}
            <InfoRow label="Overall Progress" value={`${dealProgress.progress_percent}%`} />
            <div className="flex flex-col mt-2">
              {[...dealProgress.stages].reverse().map((stage, i, arr) => (
                <div key={stage.key} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => !updatingStage && !stage.current && updateStage(stage.key)}
                      disabled={updatingStage || stage.current}
                      title={stage.current ? undefined : `Move to ${stage.label}`}
                      className={`w-2.5 h-2.5 rounded-full shrink-0 mt-0.5 p-0 border-none ${
                        stage.completed ? "bg-emerald-500" : "border-2 border-gray-300 bg-white"
                      } ${stage.current ? "ring-2 ring-emerald-200" : "cursor-pointer"}`}
                    />
                    {i < arr.length - 1 && <span className="w-px flex-1 bg-gray-200 dark:bg-gray-700 my-0.5" />}
                  </div>
                  <div className="pb-3 min-w-0">
                    <p className={`text-xs font-medium leading-tight ${stage.current ? "text-gray-800 dark:text-gray-200" : "text-gray-500 dark:text-gray-400"}`}>
                      {stage.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Deal tracking isn't available yet.</p>
        )}
      </CollapsibleSection>
    </>
  );
};

const LeadDetailsModal: React.FC<{
  lead: LeadRecord | null;
  onClose: () => void;
  initialTab?: LeadDetailsTab;
  onTagsUpdated?: (leadId: string, tags: string[]) => void;
  teamMembers?: TeamDetails[];
}> = ({ lead, onClose, initialTab = "Actions", onTagsUpdated, teamMembers = [] }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const [tab, setTab] = useState<LeadDetailsTab>(initialTab);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullLead, setFullLead] = useState<FullLead | null>(null);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [recentMessages, setRecentMessages] = useState<ChatMessage[]>([]);
  const [showFormPreview, setShowFormPreview] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookedUp, setLookedUp] = useState(false);
  const [localTags, setLocalTags] = useState<string[]>(lead?.tags || []);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingCall, setEditingCall] = useState<ScheduledCall | null>(null);

  const { makeRequest: getConversation } = useCrmApi("/conversations", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: createConversation } = useCrmApi("/conversations", "POST", {
    Authorization: `Bearer ${token}`,
  });

  // GET /conversations/{id}/insights needs a real conversation id — lead.id
  // isn't one (confirmed by testing: it 404s). `conversation` above is
  // already resolved from the lead's own conversation_id (via a real
  // GET /conversations/{id} lookup a few lines up), so use that instead.
  // A lead with no conversation yet correctly shows no insights, rather
  // than requesting insights for an id that was never a conversation.
  const { insights, loadingInsights } = useConversationInsights(conversation?.id);
  const { dealProgress, loadingDealProgress, updatingStage, updateStage } = useDealProgress(
    conversation?.id,
    conversation?.deal_stage,
    conversation?.deal_progress_percent,
  );

  // A lead with no conversation yet still needs to be schedulable — calls
  // only ever attach to a conversation (conversation-call-scheduling-
  // integration.md), so the first schedule attempt creates one on demand
  // (same POST /conversations shape the compose/new-conversation flow
  // already uses elsewhere) and the drawer adopts it going forward.
  const ensureConversationForLead = async (): Promise<string | null> => {
    if (conversation?.id) return conversation.id;
    if (!lead?.phone) {
      toast.error("This lead has no phone number on file, so a conversation can't be started yet.");
      return null;
    }
    const [response, status] = await createConversation({ title: lead.name, external_thread_id: lead.phone });
    if (status >= 200 && status < 300 && response?.id) {
      setConversation(response);
      return response.id;
    }
    toast.error(response?.detail || response?.message || "Failed to start a conversation for this lead.");
    return null;
  };

  // Lifted (rather than left inside ActionsTab) so both ActionsTab's
  // Engagement Timeline display and this drawer's own inline scheduling
  // panel share one fetch/mutation state instead of two independent ones
  // that could go stale relative to each other.
  const callsApi = useScheduledCalls(conversation?.id, ensureConversationForLead);

  useEffect(() => {
    setTab(initialTab);
    setFullLead(null);
    setConversation(null);
    setRecentMessages([]);
    setLookedUp(false);
    setLocalTags(lead?.tags || []);
    setShowFormPreview(false);

    if (!lead?.id) return;

    let cancelled = false;
    setLookupLoading(true);
    (async () => {
      // The /crm/unified list row already carries conversation_id for some
      // tenants (details.conversation_id) — use it immediately rather than
      // waiting on the separate GET /leads/{id} below, since other tenants'
      // leads don't have it populated there at all. Whichever source has
      // it, always resolve to an exact GET /conversations/{id} — never a
      // phone-search list call.
      const listConversationId = (lead.raw?.details as any)?.conversation_id as string | undefined;

      const fullLeadPromise = fetchFullLead(lead.id, token);

      let conversationId = listConversationId || undefined;
      let conv: Conversation | null = null;
      if (conversationId) {
        const [response] = await getConversation(undefined, undefined, conversationId);
        conv = response?.id ? response : null;
      }

      const full = await fullLeadPromise;
      if (cancelled) return;
      setFullLead(full);

      // Fall back to the full lead's own conversation_id whenever we still
      // don't have a resolved conversation — not just when the list payload
      // had none at all. A stale/wrong conversation_id from the list
      // response (present, but its GET /conversations/{id} came back empty)
      // was otherwise permanently blocking this from ever trying the
      // (possibly correct) id on the full lead — confirmed real: that's
      // exactly why the insights call silently never fired for some leads.
      if (!conv && full?.conversation_id && full.conversation_id !== conversationId) {
        conversationId = full.conversation_id;
        const [response] = await getConversation(undefined, undefined, conversationId);
        if (cancelled) return;
        conv = response?.id ? response : null;
      }

      setConversation(conv);

      // Recent Messages thread — GET /conversations/{id}/messages?limit=10,
      // not conversation.last_message (unreliable/not always populated).
      // Sort oldest-first for natural top-to-bottom chat reading order —
      // the API's own page order isn't guaranteed.
      if (conv?.id) {
        const [msgResponse] = await getConversation(undefined, { limit: 10 }, `${conv.id}/messages`);
        if (cancelled) return;
        const list: any[] = Array.isArray(msgResponse)
          ? msgResponse
          : Array.isArray(msgResponse?.data)
            ? msgResponse.data
            : Array.isArray(msgResponse?.messages)
              ? msgResponse.messages
              : [];
        const sorted = [...list].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
        setRecentMessages(sorted);
      } else {
        setRecentMessages([]);
      }

      setLookupLoading(false);
      setLookedUp(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  // PUT /leads/{id} is a full replace, so updateLeadSafely fetches the
  // latest lead and sends the complete record back with just `tags` changed
  // — never a partial object that would reset score/status/etc.
  const handleSaveTags = async (nextTags: string[]): Promise<boolean> => {
    if (!lead) return false;
    const { ok, lead: updated, error } = await updateLeadSafely(lead.id, { tags: nextTags }, token);
    if (ok) {
      setLocalTags(nextTags);
      if (updated) setFullLead(updated);
      onTagsUpdated?.(lead.id, nextTags);
      toast.success("Tags saved");
      return true;
    }
    toast.error(error || "Failed to save tags");
    return false;
  };

  // No notification-send endpoint exists yet — this is an in-app confirmation
  // that the owner has been flagged, per the confirmed answer for this feature.
  const handleAlertOwner = () => {
    if (!lead) return;
    const owner = teamMembers.find((m) => m.id === lead.assignedTo);
    if (!owner) {
      toast.error("This lead has no assigned owner to alert.");
      return;
    }
    toast.success(`${owner.first_name} ${owner.last_name} has been alerted about this lead.`);
  };

  // Opens the inline Schedule Call panel (which replaces the conversation
  // thread) — this is what ActionsTab's onScheduleCall override delegates to
  // instead of ActionsTab's own centered modal, since the Leads drawer has
  // its own bespoke inline design. `call` is set when reschedule-ing an
  // existing next_scheduled_call (pre-fills the panel); null for a fresh one.
  const handleOpenScheduleUi = (call?: ScheduledCall | null) => {
    setEditingCall(call || null);
    setShowScheduleModal(true);
  };

  const handleUpdateField = async (patch: LeadUpdatePatch): Promise<boolean> => {
    if (!lead) return false;
    const { ok, lead: updated, error } = await updateLeadSafely(lead.id, patch, token);
    if (ok) {
      if (updated) setFullLead(updated);
      toast.success("Saved");
      return true;
    }
    toast.error(error || "Failed to save");
    return false;
  };

  if (!lead) return null;

  const contactPreferences = fullLead?.custom?.contact_preferences;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />

      <div
        className={`fixed top-0 right-0 h-full w-full ${isFullscreen ? "" : "sm:w-[720px]"} max-w-full bg-white dark:bg-gray-900 shadow-2xl flex flex-col overflow-hidden transition-all duration-200`}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-500 dark:text-gray-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Lead Details</h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsFullscreen((f) => !f)}
              title={isFullscreen ? "Exit fullscreen" : "Expand"}
              className="p-1.5 rounded-full text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Contact banner */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 bg-emerald-50 dark:bg-emerald-500/10 border-b border-emerald-100 dark:border-emerald-500/30 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center shrink-0">
              <User size={18} className="text-indigo-500 dark:text-indigo-400" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-800 dark:text-gray-200 truncate">{displayNameFor(fullLead, lead.name)}</p>
              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">{lead.phone || "No phone on file"}</p>
            </div>
          </div>
          {dealProgress ? (
            <DealStageDropdown currentStage={dealProgress.current_stage} updating={updatingStage} onChange={updateStage} />
          ) : (
            <StatusPill status={lead.status} />
          )}
        </div>

        {/* Body: tabs on the left, conversation on the right */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          <div className="w-[300px] shrink-0 bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 flex flex-col">
            <div className="flex border-b border-gray-100 dark:border-gray-800 shrink-0">
              {(["Actions", "Insight", "Details"] as LeadDetailsTab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`flex-1 py-3 text-sm font-medium transition-colors bg-transparent border-none border-b-2 cursor-pointer ${
                    tab === t
                      ? "text-gray-900 dark:text-gray-100 border-gray-900 dark:border-gray-100"
                      : "text-[#7B7B7B] font-semibold dark:text-gray-500 border-transparent hover:text-gray-600 dark:hover:text-gray-300"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto">
              {tab === "Actions" && (
                <ActionsTab
                  insights={insights}
                  loadingInsights={loadingInsights}
                  conversationId={conversation?.id}
                  contactName={displayNameFor(fullLead, lead.name)}
                  team={teamMembers}
                  defaultAssignedTo={lead.assignedTo}
                  callsApi={callsApi}
                  onSendMessage={() => goToLeadConversation(navigate, lead)}
                  onScheduleCall={handleOpenScheduleUi}
                  onAlertOwner={handleAlertOwner}
                />
              )}
              {tab === "Insight" && (
                <InsightDataTab
                  insights={insights}
                  loadingInsights={loadingInsights}
                  tags={localTags}
                  onSaveTags={handleSaveTags}
                  contactPreferences={contactPreferences}
                />
              )}
              {tab === "Details" && (
                <DetailsPane
                  lead={lead}
                  fullLead={fullLead}
                  insights={insights}
                  loadingInsights={loadingInsights}
                  dealProgress={dealProgress}
                  loadingDealProgress={loadingDealProgress}
                  updatingStage={updatingStage}
                  updateStage={updateStage}
                  onUpdateField={handleUpdateField}
                />
              )}
            </div>
          </div>

          {/* Right: conversation thread, or the Schedule Call / Form Preview panel in its place */}
          {showFormPreview ? (
            <FormPreviewPanel leadContact={recordToContactItem(lead)} onClose={() => setShowFormPreview(false)} />
          ) : showScheduleModal ? (
            <ScheduleCallPanel
              leadName={displayNameFor(fullLead, lead.name)}
              teamMembers={teamMembers}
              defaultAssignedTo={lead.assignedTo}
              conversationId={conversation?.id}
              callsApi={callsApi}
              editingCall={editingCall}
              onClose={() => { setShowScheduleModal(false); setEditingCall(null); }}
            />
          ) : (
            <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-950 min-w-0">
              <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4 space-y-1.5">
                {lookupLoading ? (
                  <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Looking up conversation…</p>
                ) : conversation ? (
                  recentMessages.length === 0 ? (
                    <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">No messages in this conversation yet.</p>
                  ) : (
                    recentMessages.map((msg, i) => {
                      const prevDate = i > 0 ? formatDatePill(recentMessages[i - 1].created_at) : null;
                      const datePill = formatDatePill(msg.created_at);
                      return (
                        <React.Fragment key={msg.id || i}>
                          {datePill !== prevDate && (
                            <div className="flex justify-center py-2">
                              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 bg-gray-200/70 dark:bg-gray-800 rounded-full px-3 py-1">
                                {datePill}
                              </span>
                            </div>
                          )}
                          <MessageComponent msg={msg} onOpenForm={() => setShowFormPreview(true)} />
                        </React.Fragment>
                      );
                    })
                  )
                ) : lookedUp ? (
                  <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">No conversation with this lead yet.</p>
                ) : null}
              </div>

              <div className="px-5 py-3 shrink-0">
                <button
                  onClick={() => goToLeadConversation(navigate, lead)}
                  className="text-sm font-medium text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors bg-transparent border-none cursor-pointer p-0"
                >
                  View Conversation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadDetailsModal;
